import './WeatherTable.scss';
import { format } from 'date-fns';
import SortableTable from 'app/agriculture/components/Table/SortableTable';
import { useAggregateTableData } from 'app/agriculture/hooks/useAggregateChartData/useAggregateTableData';
import { convertMPStoKMPH } from 'app/agriculture/utils';
import { Interval } from 'app/agriculture/AgTimeline/types';
import { ChartInterval } from 'app/agriculture/hooks/useAggregateChartData/useAggregateChartData';
import { WindDirection } from 'app/agriculture/routes/WeatherModal/components/WindDirection';
import { useTimeZone } from 'app/agriculture/hooks/useTimeZone/useTimeZone';
import { TZDate } from '@date-fns/tz';

interface WeatherTableProps {
    dateHeader: string;
    weatherStationId: string;
    intervalUnit: ChartInterval;
    interval: Interval<Date>;
    dateFormat: string;
}

export function WeatherTable({
    dateHeader: dateHeadCellLabel,
    intervalUnit,
    weatherStationId,
    interval,
    dateFormat,
}: WeatherTableProps) {
    const { timeZone } = useTimeZone();
    const data = useAggregateTableData({
        snapshotType: 'Weather',
        deviceId: weatherStationId,
        interval,
        intervalUnit,
    });
    const mapped = data.map(
        ({
            timestamp,
            rainfallSum,
            absPressureAvg,
            gustSpeedMax,
            humidityAvg,
            windSpeedAvg,
            temperatureMin,
            temperatureAvg,
            uvIndexMax,
            windDirectionAvg,
            temperatureMax,
        }) => {
            return {
                id: timestamp,
                date: {
                    display: format(new TZDate(timestamp, timeZone), dateFormat),
                    value: Number(new Date(timestamp)),
                },
                temperatureAvg: {
                    display: temperatureAvg != null ? temperatureAvg.toFixed(1) : '-',
                    value: temperatureAvg ?? 0,
                },
                temperatureMin: {
                    display: temperatureMin != null ? temperatureMin.toFixed(1) : '-',
                    value: temperatureMin ?? 0,
                },
                temperatureMax: {
                    display: temperatureMax != null ? temperatureMax.toFixed(1) : '-',
                    value: temperatureMax ?? 0,
                },
                humidity: {
                    display: humidityAvg != null ? humidityAvg.toFixed(1) : '-',
                    value: humidityAvg ?? 0,
                },
                rainfall: {
                    display: rainfallSum != null ? rainfallSum.toFixed(1) : '-',
                    value: rainfallSum ?? 0,
                },
                absPressure: {
                    display: absPressureAvg != null ? absPressureAvg.toFixed(1) : '-',
                    value: absPressureAvg ?? 0,
                },
                windSpeed: {
                    display: convertMPStoKMPH(windSpeedAvg) ?? '-',
                    value: windSpeedAvg ?? 0,
                },
                windDirection: {
                    display:
                        windDirectionAvg != null ? <WindDirection value={windDirectionAvg} /> : '-',
                    value: windDirectionAvg ?? 0,
                },
                gustSpeed: {
                    display: convertMPStoKMPH(gustSpeedMax) ?? '-',
                    value: gustSpeedMax ?? 0,
                },
                uvIndex: {
                    display: uvIndexMax != null ? uvIndexMax.toFixed(1) : '-',
                    value: uvIndexMax ?? 0,
                },
            } as const;
        },
    );

    return (
        <div className="WeatherTable">
            <SortableTable
                data={mapped}
                headCells={[
                    { key: 'date', label: dateHeadCellLabel },
                    { key: 'rainfall', label: 'Rain (mm)' },
                    {
                        key: 'temperatureMin',
                        label: 'Temp Min (°C)',
                        isHidden: intervalUnit === 'hour',
                    },
                    {
                        key: 'temperatureMax',
                        label: 'Temp Max (°C)',
                        isHidden: intervalUnit === 'hour',
                    },
                    {
                        key: 'temperatureAvg',
                        label: 'Temp Avg (°C)',
                        isHidden: intervalUnit !== 'hour',
                    },
                    { key: 'humidity', label: 'Humidity Avg (%)' },
                    { key: 'absPressure', label: 'Pressure Avg (hPa)' },
                    { key: 'windDirection', label: 'Wind Direction Avg' },
                    { key: 'windSpeed', label: 'Wind Speed Avg (km/h)' },
                    { key: 'gustSpeed', label: 'Peak Gust Speed (km/h)' },
                    { key: 'uvIndex', label: 'UV Index Max' },
                ]}
                defaultSort={{ key: 'date', order: 'desc' }}
            />
        </div>
    );
}
